import React from 'react';
import findIndex from 'lodash/findIndex';
import {v4 as uuid} from 'uuid';

import {EMPTY_METRIC_ID, MetricGroups, useExperimentsStore} from 'apps/experiments/src/store';
import {MetricDefinition} from 'common/react-query/metrics/types';
import {ExperimentMetric, ExperimentMetricType} from 'iron/apps/experiments/src/types';

import {PageTrackingEvent, trackExperimentPageAction, TrackingEventSource} from '../tracking';
import {useUpdateExperiment} from './update-experiment';

export function useExperimentMetrics() {
  const experimentState = useExperimentsStore((state) => state.experimentState);
  const currentMetric = useExperimentsStore((state) => state.currentMetric);
  const setCurrentMetric = useExperimentsStore((state) => state.setCurrentMetric);
  const updateExperiment = useUpdateExperiment();

  const metrics = React.useMemo(() => {
    return experimentState?.metrics || [];
  }, [experimentState]);

  const primaryMetrics = React.useMemo(() => {
    return metrics.filter((metric) => metric.type === ExperimentMetricType.PRIMARY);
  }, [metrics]);

  const secondaryMetrics = React.useMemo(() => {
    return metrics.filter((metric) => metric.type === ExperimentMetricType.SECONDARY);
  }, [metrics]);

  const persistedMetrics = React.useMemo(() => {
    return metrics.filter((metric) => metric.id !== EMPTY_METRIC_ID);
  }, [metrics]);

  const persistMetrics = React.useCallback(
    (newMetrics: ExperimentMetric[], updateHistory: boolean = true) => {
      updateExperiment({metrics: newMetrics}, {updateHistory});
    },
    [metrics, updateExperiment],
  );

  const selectMetric = React.useCallback(
    (metric: ExperimentMetric | undefined, source: TrackingEventSource) => {
      setCurrentMetric(metric);
      if (metric?.id !== EMPTY_METRIC_ID) {
        setTimeout(() => persistMetrics(persistedMetrics, false), 100);
      }

      trackExperimentPageAction({
        event: metric ? PageTrackingEvent.SelectMetric : PageTrackingEvent.UnselectMetric,
        experiment: experimentState,
        source,
      });
    },
    [experimentState, setCurrentMetric, persistMetrics, persistedMetrics],
  );

  const unselectMetric = React.useCallback(
    (source: TrackingEventSource) => {
      selectMetric(undefined, source);
    },
    [selectMetric],
  );

  const createMetric = React.useCallback(
    (metricType: ExperimentMetricType, name?: string, definition?: MetricDefinition, source?: TrackingEventSource) => {
      if (!experimentState) {
        throw new Error(`cannot create metric without experimentState set`);
      }

      const newMetric: ExperimentMetric = {
        id: EMPTY_METRIC_ID,
        name: name ?? ``,
        type: metricType,
        definition: definition ?? null,
      };

      setCurrentMetric(newMetric);

      updateExperiment(
        {
          metrics:
            metricType === ExperimentMetricType.PRIMARY
              ? [...primaryMetrics, newMetric, ...secondaryMetrics]
              : [...primaryMetrics, ...secondaryMetrics, newMetric],
        },
        {persist: false, updateHistory: false},
      );

      trackExperimentPageAction({
        event: PageTrackingEvent.CreateMetric,
        experiment: experimentState,
        source,
        props: {
          'metric type': metricType,
        },
      });
    },
    [experimentState, primaryMetrics, secondaryMetrics, setCurrentMetric, updateExperiment],
  );

  const updateMetric = React.useCallback(
    (updates: Partial<ExperimentMetric>) => {
      if (!currentMetric) {
        throw new Error(`cannot update metric without currentMetric set`);
      }

      const updatedMetric = {
        ...currentMetric,
        ...updates,
      };

      const metricIndex = findIndex(metrics, (metric) => metric.id === currentMetric.id);
      if (metricIndex === -1) {
        throw new Error(`cannot update metric, not found in currentExperiment`);
      }

      if (updatedMetric.id === EMPTY_METRIC_ID) {
        updatedMetric.id = uuid();
      }

      setCurrentMetric(updatedMetric);

      const newMetrics = [...metrics.slice(0, metricIndex), updatedMetric, ...metrics.slice(metricIndex + 1)];

      persistMetrics(newMetrics);
    },
    [currentMetric, metrics, persistMetrics],
  );

  const reorderMetrics = React.useCallback(
    (metricGroups: MetricGroups) => {
      if (!experimentState) {
        throw new Error(`cannot reorder metrics without experimentState set`);
      }

      if (!metricGroups) {
        throw new Error(`cannot reorder metrics without metricGroups set`);
      }

      const newMetrics: ExperimentMetric[] = [
        ...(metricGroups[ExperimentMetricType.PRIMARY] ?? []).map((metric) => ({
          ...metric,
          type: ExperimentMetricType.PRIMARY,
        })),
        ...(metricGroups[ExperimentMetricType.SECONDARY] ?? []).map((metric) => ({
          ...metric,
          type: ExperimentMetricType.SECONDARY,
        })),
      ];

      persistMetrics(newMetrics);

      trackExperimentPageAction({
        event: PageTrackingEvent.ReorderMetric,
        experiment: experimentState,
      });
    },
    [experimentState, currentMetric, metrics, persistMetrics],
  );

  const deleteMetric = React.useCallback(
    (id: string) => {
      const newMetrics = persistedMetrics.filter((metric) => metric.id !== id);

      persistMetrics(newMetrics);

      trackExperimentPageAction({
        event: PageTrackingEvent.DeleteMetric,
        experiment: experimentState,
      });
    },
    [experimentState, currentMetric, persistedMetrics, persistMetrics],
  );

  return {
    metrics,
    primaryMetrics,
    secondaryMetrics,
    selectMetric,
    unselectMetric,
    createMetric,
    updateMetric,
    reorderMetrics,
    deleteMetric,
  };
}
